import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';
import type {
  AMLForm,
  AMLFormCreate,
  AMLFormUpdate,
  AMLFormPublicView,
  AMLFormPublicUpdate,
  AMLFormLinkInfo,
  AMLFormListResponse,
  AMLFormDocument,
  DocumentType,
} from '@/types/aml-forms';

const BASE_PATH = ROUTES.API.AML_FORMS;

/**
 * Get list of AML forms with pagination
 */
export const getAMLForms = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<AMLFormListResponse> => {
  const response = await api.get<AMLFormListResponse>(BASE_PATH + '/', { params });
  return response.data;
};

/**
 * Get a specific AML form by ID
 */
export const getAMLForm = async (id: number): Promise<AMLForm> => {
  const response = await api.get<AMLForm>(`${BASE_PATH}/${id}/`);
  return response.data;
};

/**
 * Create a new AML form
 */
export const createAMLForm = async (data: AMLFormCreate): Promise<AMLForm> => {
  const response = await api.post<AMLForm>(BASE_PATH + '/', data);
  return response.data;
};

/**
 * Update an existing AML form
 */
export const updateAMLForm = async (id: number, data: AMLFormUpdate): Promise<AMLForm> => {
  const response = await api.patch<AMLForm>(`${BASE_PATH}/${id}/`, data);
  return response.data;
};

/**
 * Delete an AML form
 */
export const deleteAMLForm = async (id: number): Promise<void> => {
  await api.delete(`${BASE_PATH}/${id}/`);
};

/**
 * Toggle the checked status of an AML form
 */
export const toggleAMLFormChecked = async (id: number, isChecked: boolean): Promise<AMLForm> => {
  const response = await api.patch<AMLForm>(`${BASE_PATH}/${id}/`, { is_checked: isChecked });
  return response.data;
};

/**
 * Get link information for an AML form
 */
export const getAMLFormLinkInfo = async (id: number): Promise<AMLFormLinkInfo> => {
  const response = await api.get<AMLFormLinkInfo>(`${BASE_PATH}/${id}/link_info/`);
  return response.data;
};

/**
 * Regenerate token for an AML form
 */
export const regenerateAMLFormToken = async (id: number): Promise<AMLForm> => {
  const response = await api.post<AMLForm>(`${BASE_PATH}/${id}/regenerate_token/`);
  return response.data;
};

/**
 * Get AML form by public token (no authentication required)
 */
export const getAMLFormByToken = async (token: string): Promise<AMLFormPublicView> => {
  const response = await api.get<AMLFormPublicView>(`${BASE_PATH}/public/${token}/`);
  return response.data;
};

/**
 * Update AML form by public token (no authentication required)
 */
export const updateAMLFormByToken = async (
  token: string,
  data: AMLFormPublicUpdate
): Promise<AMLFormPublicView> => {
  const response = await api.patch<AMLFormPublicView>(`${BASE_PATH}/public/${token}/update/`, data);
  return response.data;
};

// =============================================================================
// Document API Functions - Public (No Authentication)
// =============================================================================

/**
 * Upload a document to a form (public - for client form filling)
 */
export const uploadDocumentPublic = async (
  token: string,
  file: File,
  options?: {
    document_type?: DocumentType;
    description?: string;
    field_id?: string;
  }
): Promise<AMLFormDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.document_type) formData.append('document_type', options.document_type);
  if (options?.description) formData.append('description', options.description);
  if (options?.field_id) formData.append('field_id', options.field_id);

  const response = await api.post<AMLFormDocument>(
    `${BASE_PATH}/public/${token}/documents/upload/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/**
 * Download a document (public - for client form filling)
 */
export const downloadDocumentPublic = async (
  formToken: string,
  documentToken: string
): Promise<Blob> => {
  const response = await api.get(
    `${BASE_PATH}/public/${formToken}/documents/${documentToken}/download/`,
    { responseType: 'blob' }
  );
  return response.data;
};

/**
 * Delete a client-uploaded document (public)
 */
export const deleteDocumentPublic = async (
  formToken: string,
  documentToken: string
): Promise<void> => {
  await api.delete(`${BASE_PATH}/public/${formToken}/documents/${documentToken}/`);
};

// =============================================================================
// Document API Functions - Authenticated (for firm staff)
// =============================================================================

/**
 * Upload a document to a form (authenticated - for staff)
 */
export const uploadDocumentAuthenticated = async (
  formId: number,
  file: File,
  options?: {
    document_type?: DocumentType;
    description?: string;
    field_id?: string;
  }
): Promise<AMLFormDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.document_type) formData.append('document_type', options.document_type);
  if (options?.description) formData.append('description', options.description);
  if (options?.field_id) formData.append('field_id', options.field_id);

  const response = await api.post<AMLFormDocument>(
    `${BASE_PATH}/${formId}/upload_document/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/**
 * List all documents for a form (authenticated)
 */
export const listFormDocuments = async (formId: number): Promise<AMLFormDocument[]> => {
  const response = await api.get<AMLFormDocument[]>(`${BASE_PATH}/${formId}/documents/`);
  return response.data;
};

/**
 * Download a document (authenticated - for staff)
 */
export const downloadDocumentAuthenticated = async (
  formId: number,
  documentId: number
): Promise<Blob> => {
  const response = await api.get(
    `${BASE_PATH}/${formId}/documents/${documentId}/download/`,
    { responseType: 'blob' }
  );
  return response.data;
};

/**
 * Delete a document (authenticated - for staff)
 */
export const deleteDocumentAuthenticated = async (
  formId: number,
  documentId: number
): Promise<void> => {
  await api.delete(`${BASE_PATH}/${formId}/documents/${documentId}/`);
};

/**
 * Get public download URL for a document
 */
export const getDocumentPublicUrl = (formToken: string, documentToken: string): string => {
  const baseUrl = import.meta.env.VITE_API_URL || 'https://go.sentra.be';
  return `${baseUrl}${BASE_PATH}/public/${formToken}/documents/${documentToken}/download/`;
};
